import React, { JSX } from 'react';
import { observer } from 'mobx-react';
import PaxRoomSelectionController from './PaxRoomSelectionController';
import useTranslation from '../../../Hooks/useTranslation';
import fallback from '../../../../_Translations/generated/pax-room-selection.en.t.json';
import { Col, Label, ListGroupItem, Row } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChild } from '@fortawesome/free-solid-svg-icons';
import Keys from '../../../../_Translations/generated/pax-room-selection.en.t.json.keys';
import { Select } from '../../Form/Select/Select';
import { range } from 'lodash-es';
import { ReactSelectOption } from '../../../../Models/ReactSelectOption';

const ChildAgePicker = observer(
  ({
    roomIndex,
    childIndex,
    childAgePlaceholder,
    minSelectableChildAge,
    maxChildAge,
    state,
    disabled,
    plusMinusMode,
    searchPaxAutoFocus,
    searchPaxInputSearchable,
    revalidateChildrenOnChangeAfterError,
    validateChildrenSelection,
    paxHasError,
    menuPortalTarget,
    onChange
  }: {
    roomIndex: number;
    childIndex: number;
    childAgePlaceholder?: string;
    minSelectableChildAge?: number;
    maxChildAge?: number;
    state: PaxRoomSelectionController;
    disabled?: boolean;
    plusMinusMode?: boolean;
    searchPaxInputSearchable?: boolean;
    searchPaxAutoFocus?: boolean;
    revalidateChildrenOnChangeAfterError?: boolean;
    validateChildrenSelection?: () => boolean;
    paxHasError?: boolean;
    menuPortalTarget?: HTMLElement;
    onChange?: (roomIndex: number, childIndex: number, newValue: ReactSelectOption) => void;
  }): JSX.Element => {
    const { t } = useTranslation('pax-room-selection', fallback);
    const finalMaxChildAge = maxChildAge ? maxChildAge + 1 : state.defaultChildAgeLimit;

    return plusMinusMode ? (
      <>
        <Label className="paxRoomSelection__child-age-picker__label">{`${t(
          Keys.ageCapitalized
        )} - ${t(Keys.child)} ${childIndex + 1}`}</Label>
        <Select
          placeholder={childAgePlaceholder}
          options={range(minSelectableChildAge || 0, finalMaxChildAge).map(val => {
            return state.getChildAgeOption(val);
          })}
          selectedOption={
            state.rooms[roomIndex].childrenAges.length > childIndex &&
            !Number.isNaN(state.rooms[roomIndex].childrenAges[childIndex])
              ? state.getSelectedChildAgeOption(state.rooms[roomIndex].childrenAges[childIndex])
              : null
          }
          getLabel={(option): string => (option ? option.label : '')}
          getValue={(option): string => (option ? option.value : '')}
          onChange={(val): void =>
            onChange
              ? onChange(roomIndex, childIndex, val)
              : state.setChildAgeForRoom(roomIndex, childIndex, val)
          }
          isSearchable={searchPaxInputSearchable}
          autoFocus={searchPaxAutoFocus}
          isDisabled={disabled}
          menuPlacement={'auto'}
          menuPortalTarget={menuPortalTarget}
        />
      </>
    ) : (
      <ListGroupItem key={roomIndex + childIndex}>
        <Row className="ml-1 no-gutters">
          <Col className="my-auto col-sm-6">
            <FontAwesomeIcon icon={faChild} className="fa-lg mr-1" />
            <span>
              {t(Keys.child)} {childIndex + 1} ({t(Keys.age)})
            </span>
          </Col>
          <Col className="col-sm-6">
            <Select
              placeholder={childAgePlaceholder}
              options={range(minSelectableChildAge || 0, finalMaxChildAge).map(val => {
                return state.getChildAgeOption(val);
              })}
              selectedOption={
                state.rooms[roomIndex].childrenAges.length > childIndex &&
                !Number.isNaN(state.rooms[roomIndex].childrenAges[childIndex])
                  ? state.getSelectedChildAgeOption(state.rooms[roomIndex].childrenAges[childIndex])
                  : null
              }
              getLabel={(option): string => (option ? option.label : '')}
              getValue={(option): string => (option ? option.value : '')}
              onChange={(val): void => {
                state.setChildAgeForRoom(roomIndex, childIndex, val);
                if (
                  revalidateChildrenOnChangeAfterError &&
                  validateChildrenSelection &&
                  paxHasError
                ) {
                  validateChildrenSelection();
                }
              }}
              isSearchable={searchPaxInputSearchable}
              autoFocus={searchPaxAutoFocus}
              isDisabled={disabled}
              menuPlacement={'auto'}
              menuPortalTarget={menuPortalTarget}
            />
          </Col>
        </Row>
      </ListGroupItem>
    );
  }
);

export default ChildAgePicker;
